import React, { memo } from "react";

import { ShowcaseCard, ShowcaseIntro, ShowcaseNotice } from "ui/components/Showcase";

import { JomImportIntroProps } from "./types";

/** Primjer popisa dok nijedno vozilo nije dodano. */
const SAMPLE_ITEMS = [
  { jom: "104223", name: "Kia Sorento 2.2 CRDi", plate: "ZG 1234 AB" },
  { jom: "098771", name: "Škoda Octavia 2.0 TDI", plate: "ZG 5678 CD" },
  { jom: "112540", name: "VW Golf 1.5 TSI", plate: "" },
];

const SAMPLE_CATALOG = {
  name: "Kia Sorento 2.2 CRDi",
  image: "/media/images/vehicles/vehicle-2.png",
  details: "2021 · 148 kW · Dizel",
};

/**
 * Lijevi panel uvoza po JOM broju: popis vozila za uvoz, vozilo pronađeno u katalogu i podaci
 * koji stižu iz kataloga.
 */
const JomImportIntro: React.FC<JomImportIntroProps> = ({ items, selectedVehicle }) => {
  const rows =
    items.length > 0
      ? items.map((item) => ({
          jom: item.jomNumber,
          name: item.vehicle?.name ?? "",
          plate: item.registrationNumber,
        }))
      : SAMPLE_ITEMS;

  const catalogName = selectedVehicle?.name ?? SAMPLE_CATALOG.name;
  const catalogImage = selectedVehicle?.image || SAMPLE_CATALOG.image;
  const catalogDetails = selectedVehicle
    ? [selectedVehicle.yearFrom, selectedVehicle.power ? `${selectedVehicle.power} kW` : null, selectedVehicle.fuelType]
        .filter(Boolean)
        .join(" · ")
    : SAMPLE_CATALOG.details;

  return (
    <ShowcaseIntro
      sceneKey="jom-import"
      title="Uvezite vozila preko JOM broja"
      description="Upišite JOM broj za svako vozilo. Naziv, snagu i gorivo sustav preuzima iz kataloga."
    >
      <ShowcaseCard className="left-[0.5rem] top-[-1rem] w-[17rem] h-[18rem]" delay={0}>
        <div className="p-5">
          <div className="text-[0.65rem] font-semibold uppercase tracking-wide text-slate-400">Za uvoz</div>
          <div className="text-sm font-bold text-slate-800">
            {items.length > 0 ? `${items.length} vozila` : "Popis vozila"}
          </div>
          <div className="mt-3 space-y-2">
            {rows.slice(0, 4).map((row, index) => (
              <div key={index} className="rounded-md bg-slate-50 px-3 py-2">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[0.6rem] font-semibold text-blue-600">JOM {row.jom}</span>
                  {row.plate && <span className="text-[0.6rem] text-slate-400">{row.plate}</span>}
                </div>
                <div className="text-[0.7rem] font-medium text-slate-700 truncate">{row.name || "Vozilo iz kataloga"}</div>
              </div>
            ))}
          </div>
        </div>
      </ShowcaseCard>

      <ShowcaseCard className="right-[-0.5rem] top-[6rem] w-[14rem]" delay={0.25}>
        <div className="p-4">
          <div className="text-[0.65rem] font-semibold uppercase tracking-wide text-slate-400">Pronađeno u katalogu</div>
          <div className="mt-2 h-16 overflow-hidden">
            <img src={catalogImage} alt="" className="w-full h-full object-contain scale-[1.3]" />
          </div>
          <div className="mt-2 text-xs font-bold text-slate-800 truncate">{catalogName}</div>
          {catalogDetails && <div className="text-[0.65rem] text-slate-400 truncate">{catalogDetails}</div>}
        </div>
      </ShowcaseCard>

      <ShowcaseCard className="left-[-1.5rem] bottom-[-0.5rem] w-[15rem]" delay={0.5}>
        <ShowcaseNotice
          title="Podaci iz kataloga"
          subtitle="Bez ručnog upisa marke i modela"
          iconClassName="bg-blue-100"
          icon={
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="#1D4ED8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <ellipse cx="12" cy="5" rx="9" ry="3" />
              <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
            </svg>
          }
        />
      </ShowcaseCard>
    </ShowcaseIntro>
  );
};

export default memo(JomImportIntro);
